import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Card, Button, Spinner, Alert, Row, Col, Badge, Tabs, Tab, Table } from 'react-bootstrap';
import { FaEdit } from 'react-icons/fa';
import { saveAs } from 'file-saver';

import VereinbarungService from '../services/vereinbarung.service';

const VereinbarungDetail = () => {
    const { id } = useParams();
    const [agreement, setAgreement] = useState(null);
    const [history, setHistory] = useState([]);
    const [log, setLog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        setLoading(true);
        // Alle Daten parallel abrufen
        Promise.all([
            VereinbarungService.getAgreementById(id),
            VereinbarungService.getAgreementHistory(id),
            VereinbarungService.getAgreementLog(id)
        ]).then(([agreementRes, historyRes, logRes]) => {
            setAgreement(agreementRes.data);
            setHistory(historyRes.data);
            setLog(logRes.data);
            setLoading(false);
        }).catch(error => {
            const errorMessage = (error.response?.data?.message) || error.message || error.toString();
            setError(errorMessage);
            setLoading(false);
        });
    }, [id]);

const handleDownloadPdf = () => {
    console.log("=== PDF Download Start ===");
    setError(''); // Fehler zurücksetzen

    VereinbarungService.downloadAgreementPdf(id)
        .then(response => {
            console.log("Response Data:", response.data);
            
            let pdfBlob;
            
            // Prüfen ob die Daten als JSON-Objekt kommen (temporärer Fix)
            if (response.data instanceof Blob) {
                // Normaler Fall: Blob direkt verwenden
                pdfBlob = response.data;
            } else if (typeof response.data === 'object' && !Array.isArray(response.data)) {
                // Workaround: JSON-Objekt zu Uint8Array konvertieren
                console.log("Konvertiere JSON zu Binary...");
                const values = Object.values(response.data);
                const uint8Array = new Uint8Array(values);
                pdfBlob = new Blob([uint8Array], { type: 'application/pdf' });
            } else {
                console.error("Unerwartetes Datenformat:", typeof response.data);
                setError("Unerwartetes PDF-Format vom Server.");
                return;
            }
            
            console.log("PDF Blob Größe:", pdfBlob.size);
            
            if (pdfBlob.size === 0) {
                setError("Das generierte PDF ist leer.");
                return;
            }
            
            // Erstelle Download-Link
            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            const filename = `vereinbarung_${id}_v${agreement?.version || 'unbekannt'}.pdf`;
            link.setAttribute('download', filename);
            
            // Download auslösen
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Aufräumen
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 100);
            
            console.log("=== PDF Download Ende ===");
        })
        .catch(err => {
            console.error("PDF Download Fehler:", err);
            
            if (err.response) {
                const errorMessage = err.response.data?.message || 
                                   `Serverfehler: ${err.response.status}`;
                setError(errorMessage);
            } else if (err.request) {
                setError("Keine Antwort vom Server.");
            } else {
                setError("Ein unerwarteter Fehler ist aufgetreten.");
            }
        });
};

    if (loading) {
        return <div className="text-center"><Spinner animation="border" /></div>;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    if (!agreement) {
        return <Alert variant="warning">Keine Vereinbarungsdaten gefunden.</Alert>;
    }
    
    const getStatusBadge = (status) => {
        switch (status) {
            case 'Entwurf':
                return <Badge bg="secondary">Entwurf</Badge>;
            case 'Unterzeichnet':
                return <Badge bg="success">Unterzeichnet</Badge>;
            case 'Archiviert':
                return <Badge bg="warning" text="dark">Archiviert</Badge>;
            default:
                return <Badge bg="info">{status}</Badge>;
        }
    };

    return (
        <Container>
            <Row className="my-3">
                <Col>
                    <h1>{agreement.individueller_titel ? agreement.individueller_titel : agreement.titel.titel} <small className="text-muted">(v{agreement.version})</small></h1>
                    {agreement.individueller_titel && agreement.titel && agreement.titel.titel && (
                        <div className="text-muted">Vorlage: {agreement.titel.titel}</div>
                    )}
                </Col>



                <Col className="d-flex justify-content-end align-items-center">
                    <Button variant="secondary" onClick={handleDownloadPdf} className="me-2">
                        Download als PDF
                    </Button>
                    <Button as={Link} to={`/agreements/${id}/edit`} variant="warning" className="me-2" title="Bearbeiten" aria-label="Bearbeiten">
                        <FaEdit /> Bearbeiten
                    </Button>
                    <Button as={Link} to={`/agreements/${id}/version-erstellen`} variant="success">
                        Neue Version erstellen
                    </Button>
                </Col>
                
                
                
               
            </Row>
            <Card className="mb-3">
                <Card.Header>Details</Card.Header>
                <Card.Body>
                    <p><strong>Partner:</strong> {agreement.ersteller.id === agreement.empfaenger.id ? "Intern" : agreement.empfaenger.username}</p>
                    <p><strong>Status:</strong> {getStatusBadge(agreement.status)}</p>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header>Inhalt der Vereinbarung</Card.Header>



                <Card.Body>
                    <div 
                        style={{
                            lineHeight: '1.2',
                            fontSize: '14px'
                        }}
                        className="agreement-content"
                        dangerouslySetInnerHTML={{ __html: agreement.inhalt }} 
                    />
                    <style>{`
                        .agreement-content p {
                            margin-bottom: 6px !important;
                            line-height: 1.2 !important;
                        }
                        .agreement-content p:first-child {
                            margin-top: 0 !important;
                        }
                        .agreement-content p:last-child {
                            margin-bottom: 0 !important;
                        }
                        .agreement-content br + br {
                            display: none;
                        }
                        .agreement-content h1,
                        .agreement-content h2,
                        .agreement-content h3 {
                            line-height: 1.2 !important;
                            margin-top: 10px !important;
                            margin-bottom: 8px !important;
                        }
                        .agreement-content ul,
                        .agreement-content ol {
                            margin-bottom: 6px !important;
                            line-height: 1.2 !important;
                        }
                        .agreement-content li {
                            margin-bottom: 2px !important;
                            line-height: 1.2 !important;
                        }
                    `}</style>
                </Card.Body>
                
                
                
  
            </Card>

            <Card className="mt-3">
                 <Card.Header>Versionshistorie & Protokoll</Card.Header>
                 <Card.Body>
                    <Tabs defaultActiveKey="history" id="agreement-details-tabs">
                        <Tab eventKey="history" title="Versionshistorie">
                            <Table striped bordered hover responsive size="sm" className="mt-3">
                                <thead>
                                    <tr>
                                        <th>Version</th>
                                        <th>Status</th>
                                        <th>Erstellt am</th>
                                        <th>Aktion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map(item => (
                                        <tr key={item.vereinbarung_id}>
                                            <td>v{item.version}</td>
                                            <td><Badge bg={item.status === 'Archiviert' ? 'warning' : 'secondary'}>{item.status}</Badge></td>
                                            <td>{new Date(item.createdAt).toLocaleString()}</td>
                                            <td>
                                                <Button as={Link} to={`/agreements/${item.vereinbarung_id}`} variant="outline-primary" size="sm" disabled={item.vereinbarung_id === agreement.vereinbarung_id}>
                                                    Anzeigen
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Tab>
                        <Tab eventKey="log" title="Aktionsprotokoll">
                             <Table striped bordered hover responsive size="sm" className="mt-3">
                                <thead>
                                    <tr>
                                        <th>Zeitstempel</th>
                                        <th>Benutzer</th>
                                        <th>Aktion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {log.map(entry => (
                                        <tr key={entry.log_id}>
                                            <td>{new Date(entry.createdAt).toLocaleString()}</td>
                                            <td>{entry.user ? entry.user.username : 'N/A'}</td>
                                            <td>{entry.aktion}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Tab>
                    </Tabs>
                 </Card.Body>
            </Card>
        </Container>
    );
};

export default VereinbarungDetail;